import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

export default function Process() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const topSteps = [
    {
      title: 'Describe Requirements',
      description: 'Share your technology needs via our contact form',
      icon: 'ri-clipboard-line',
    },
    {
      title: 'Review Resume',
      description: 'Evaluate candidates matching your specifications',
      icon: 'ri-file-list-3-line',
    },
    {
      title: 'Evaluate Skills & Expertise',
      description: 'Assess through coding challenges and technical evaluations',
      icon: 'ri-shield-check-line',
    },
    {
      title: 'Sign Contract',
      description: 'Launch your remote team with the perfect candidate',
      icon: 'ri-file-edit-line',
    },
  ];

  const bottomSteps = [
    {
      title: 'New Profile',
      description: 'Get the new resource that matches your requirements',
      icon: 'ri-user-add-line',
    },
    {
      title: 'Not Satisfied',
      description: 'If our candidate does not meet your requirements',
      icon: 'ri-user-unfollow-line',
    },
  ];

  return (
    <section id="process" className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-list-check-2 text-primary-600"></i>
            <span className="text-sm font-medium text-gray-700">How It Works</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Our Hiring
            <br />
            <span className="text-primary-600">
              Process
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            We follow a structured, outcome-focused approach to help you scale your development with the right expertise.
          </motion.p>
        </motion.div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Top Row - Main Process Steps */}
          <div className="relative">
            {/* Connecting Line with Arrows - Desktop */}
            <div className="hidden lg:flex absolute top-[88px] left-[12%] right-[12%] h-0 items-center justify-between">
              <div className="flex-1 h-0.5 bg-primary-400"></div>
              <i className="ri-arrow-right-s-line text-xl text-primary-400 mx-1 w-5 h-6.5 bg-white absolute left-[15%] right-[15%] items-center justify-between"></i>
              <div className="flex-1 h-0.5 bg-primary-400"></div>
              <i className="ri-arrow-right-s-line text-xl text-primary-400 mx-1 w-5 h-6.5 bg-white absolute left-[48%] right-[48%] items-center justify-between"></i>
              <div className="flex-1 h-0.5 bg-primary-400"></div>
              <i className="ri-arrow-right-s-line text-xl text-primary-400 mx-1 w-5 h-6.5 bg-white absolute left-[83%] right-[83%] items-center justify-between"></i>
              <div className="flex-1 h-0.5 bg-primary-400"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
              {topSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50, scale: 0.9 }}
                  animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                  transition={{ ...springTransition, duration: 0.7, delay: index * 0.12 }}
                  className="relative flex flex-col items-center text-center"
                >
                  {/* Icon */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: index * 0.12 + 0.1, ...springTransition }}
                    className="relative mb-10"
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-white border-2 border-primary-500 rounded-xl shadow-sm">
                      <i className={`${step.icon} text-2xl md:text-3xl text-primary-600`}></i>
                    </div>
                    {/* Connector Dot */}
                    <div className="hidden lg:block absolute top-full left-1/2 -translate-x-1/2 mt-4">
                      <div className="w-4 h-4 rounded-full border-2 border-primary-500 bg-white flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-500"></div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Content */}
                  <motion.h3
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: index * 0.12 + 0.15, duration: 0.5 }}
                    className="text-base md:text-lg font-bold text-navy-900 mb-1"
                  >
                    {step.title}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: index * 0.12 + 0.2, duration: 0.5 }}
                    className="text-xs md:text-sm text-gray-500 max-w-[200px]"
                  >
                    {step.description}
                  </motion.p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Middle Spacing */}
          <div className="hidden lg:block h-0"></div>

          {/* Feedback Loop - Bottom Row */}
          <div className="hidden lg:block relative mt-8">
            {/* Dashed Loop Line */}
            <svg
              className="absolute top-0 left-0 w-full h-32 -mt-4"
              style={{ overflow: 'visible' }}
              viewBox="0 0 1200 130"
              preserveAspectRatio="none"
            >
              {/* Left curve from Review Resume down */}
              <path
                d="M 300 0 L 300 60 Q 300 90 330 90 L 500 90"
                fill="none"
                stroke="#95c302"
                strokeWidth="2"
                strokeDasharray="8 6"
                opacity="0.5"
              />
              {/* Right curve to Sign Contract */}
              <path
                d="M 700 90 L 870 90 Q 900 90 900 60 L 900 0"
                fill="none"
                stroke="#95c302"
                strokeWidth="2"
                strokeDasharray="8 6"
                opacity="0.5"
              />
              {/* Bottom connecting line with arrows */}
              <path
                d="M 500 90 L 700 90"
                fill="none"
                stroke="#95c302"
                strokeWidth="2"
                strokeDasharray="8 6"
                opacity="0.5"
              />
              {/* Arrow markers */}
              <polygon points="670,85 660,90 670,95" fill="#95c302" opacity="0.5" />
              <polygon points="670,85 660,90 670,95" fill="#95c302" opacity="0.5" />
            </svg>

            {/* Bottom Steps */}
            <div className="grid grid-cols-2 gap-4 max-w-xl mx-auto pt-24">
              {bottomSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                  transition={{ ...springTransition, duration: 0.7, delay: 0.6 + index * 0.15 }}
                  className="flex flex-col items-center text-center"
                >
                  {/* Icon */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.7 + index * 0.15, ...springTransition }}
                    className="relative mb-3"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-white border-2 border-primary-400 rounded-xl shadow-sm">
                      <i className={`${step.icon} text-xl text-primary-500`}></i>
                    </div>
                  </motion.div>

                  {/* Content */}
                  <motion.h3
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.75 + index * 0.15, duration: 0.5 }}
                    className="text-sm md:text-base font-bold text-navy-900 mb-1"
                  >
                    {step.title}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0, y: 15 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.8 + index * 0.15, duration: 0.5 }}
                    className="text-xs text-gray-500 max-w-[180px]"
                  >
                    {step.description}
                  </motion.p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mobile/Tablet: Simple Feedback Note */}
          <div className="lg:hidden mt-8 pt-8 border-t border-gray-100">
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              {bottomSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30, scale: 0.9 }}
                  animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                  transition={{ ...springTransition, duration: 0.7, delay: 0.6 + index * 0.15 }}
                  className="flex items-start gap-3 max-w-[260px]"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.7 + index * 0.15, ...springTransition }}
                    className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-white border-2 border-primary-400 rounded-lg shadow-sm"
                  >
                    <i className={`${step.icon} text-lg text-primary-500`}></i>
                  </motion.div>
                  <div>
                    <motion.h4
                      initial={{ opacity: 0, x: -10 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.75 + index * 0.15, duration: 0.4 }}
                      className="text-sm font-bold text-navy-900"
                    >
                      {step.title}
                    </motion.h4>
                    <motion.p
                      initial={{ opacity: 0, x: -10 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.8 + index * 0.15, duration: 0.4 }}
                      className="text-xs text-gray-500"
                    >
                      {step.description}
                    </motion.p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}